import React, { FC, useCallback, useEffect, useRef } from 'react';
import * as Blockly from 'blockly';
import { WorkspaceSvg } from 'react-blockly';
import XMLParser from 'react-xml-parser';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { javascriptGenerator } from 'blockly/javascript';
import { toast } from 'react-toastify';

import { CompositeMetricData } from '@app/interfaces/metric.type';
import useTranslation from '@app/hooks/use-translation';

import { buildingJsonFromMetricFormula } from './data/json-rebuild.data';
import { useToolboxCategories } from './data/toolbox-categories.data';

type BlocklyNativeProps = {
  initialXml: string;
  onChange: ({ formula }: { formula: CompositeMetricData['formula'] }) => void;
  onCode: (code: string) => void;
};

const BlocklyNative: FC<BlocklyNativeProps> = (props) => {
  const { initialXml, onChange, onCode } = props;
  const BLOCK_ELEMENT = 'blocklyDiv';
  const { toolboxCategories } = useToolboxCategories();
  const { t } = useTranslation('pages.metric');
  const blocklyRef = useRef<HTMLDivElement>(null);
  const blocklyBlock = useRef<WorkspaceSvg | null>(null);
  const xmlChangeHandler = useCallback(
    (event) => {
      function ignoreParserException(xmlWorkspace: Element): {
        children: Array<NonNullable<unknown>>;
      } {
        function parseXMLString() {
          const xmlString = new XMLSerializer().serializeToString(xmlWorkspace);
          return new XMLParser().parseFromString(xmlString, 'text/xml');
        }
        try {
          return parseXMLString();
        } catch (error) {
          console.log((error as Error).message);
          return parseXMLString();
        }
      }
      const eventListToReload = [
        'finished_loading',
        'create',
        'move',
        'change',
        'delete',
        'block_field_intermediate_change',
      ];
      if (eventListToReload.includes(event.type)) {
        if (blocklyBlock?.current && Blockly?.Xml.workspaceToDom(blocklyBlock.current)) {
          const xmlWorkspace = Blockly?.Xml.workspaceToDom(blocklyBlock.current);
          onChange({
            formula: buildingJsonFromMetricFormula(
              ignoreParserException(xmlWorkspace).children[0],
              'formula',
            ),
          });
          onCode(javascriptGenerator.workspaceToCode(blocklyBlock.current));
        }
      }
    },
    [onChange, onCode],
  );

  useEffect(() => {
    try {
      if (blocklyRef && !blocklyBlock?.current) {
        blocklyBlock.current = Blockly.inject(BLOCK_ELEMENT, {
          toolbox: toolboxCategories,
          sounds: false,
          zoom: {
            controls: true,
            wheel: true,
            startScale: 1.0,
            maxScale: 3,
            minScale: 0.3,
            scaleSpeed: 1.2,
            pinch: true,
          },
        });
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(initialXml, 'text/xml').documentElement;
        setTimeout(() => {
          if (blocklyBlock.current) {
            Blockly.Xml.appendDomToWorkspace(doc, blocklyBlock.current);
            blocklyBlock.current.addChangeListener(xmlChangeHandler);
          }
        }, 0);
      }
    } catch (error) {
      console.error(error);
      toast.error(t('error_metric_build'));
    }
  }, [initialXml, onCode, t, toolboxCategories, xmlChangeHandler]);

  return <div id={BLOCK_ELEMENT} className="h-[100vh] w-[100%] z-50" />;
};
export default BlocklyNative;
